/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import { type AbstractControl, type ValidationErrors, Validators } from '@angular/forms';
import {
    type Capacity, type Command, CAPACITY_UNIT, Consts,
    DMValidators, FormControl, FormGroup, FormSlideWindowAbstract, UNPLACEHOLDER,
    UtilService,
} from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { ToolService } from 'app/common/services';
import { OnDemandPolicy } from 'app/common/types/mo';
import { merge } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';

import { AntivirusService } from '../../../antivirus.service';
import { AntivirusStrService, DurationTypeE, ScheduleTypeE } from '../../../antivirus-str.service';

export interface PolicyVm {
    /**
     * 策略名称
     */
    name?: string;
    /**
     * 调度类型
     */
    scheduleType?: ScheduleTypeE;
    /**
     * 不扫描文件类型
     */
    excludeFileType?: string[];
    /**
     * 不扫描文件路径
     */
    excludeFilePath?: string[];
    /**
     * 是否限制最大可扫描文件大小
     */
    restricted?: boolean;
    /**
     * 最大可扫描文件大小容量
     */
    restrictCapacity?: Capacity;
    /**
     * 持续时间类型
     */
    durationType?: DurationTypeE;
    /**
     * 每周的天数
     */
    days?: number[];
    /**
     * 指定周期
     */
    executePeriod?: number;
    /**
     * 开始时间
     */
    startTime?: string;
    /**
     * 持续时间小时数
     */
    durationHour?: number;
    /**
     * 持续时间分钟数
     */
    durationMinute?: number;
}
@Component({
    selector: 'app-create-on-demand-scan',
    templateUrl: './create-on-demand-scan.component.html',
    styleUrls: ['./create-on-demand-scan.component.scss'],
})
export class CreateOnDemandScanComponent extends FormSlideWindowAbstract implements OnInit {
    @Input() policyData: OnDemandPolicy;
    @Input() vstoreId: string;

    i18n = this.str.i18n;
    ScheduleTypeE = ScheduleTypeE;
    DurationTypeE = DurationTypeE;

    modifyVmValue: PolicyVm;
    vm: FormGroup;
    errorMessage: any;
    weekList: SelectOption[] = this.str.weekList;
    scheduleTypeList: SelectOption[] = this.str.scheduleTypeList;
    durationTypeList: SelectOption[] = this.str.durationTypeList;
    maxScanCapacityUnit: string[] = this.str.maxScanCapacityUnit;
    // 默认开始时间
    defaultStartTime = '22:00';
    // 文件扫描最小限制
    fileSizeMin = '1 MB';
    // 文件扫描最大限制
    fileSizeMax = '4 GB';
    // 名称长度
    nameMinLen = 1;
    nameMaxLen = 127;
    // 单个不扫描文件类型长度
    fileTypeMinLen = 1;
    fileTypeMaxLen = 8;
    // 侧滑框标题
    title = this.i18n.createOnDemandPolicy;
    fileTypeSeperator = [',', ';'];
    filePathSeperator = this.str.filePathSeperator;

    closeArgs = {
        refresh: false,
        data: null,
    };

    /** 最大可扫描文件取值的范围 */
    restrictCapacityRange = this.str.getRangeTip(this.fileSizeMin, this.fileSizeMax);

    constructor(
        injector: Injector,
        private str: AntivirusStrService,
        private service: AntivirusService,
        private toolService: ToolService,
        private utilService: UtilService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        super.ngOnInit();

        // 修改入口初始化参数
        if (this.policyData) {
            this.initModifyValue();
            this.title = this.i18n.modifyOnDemandPolicy;
        }

        this.checkVmDisabled();

        this.pageReady = true;
        this.windowVisible = true;
    }

    initVm(): void {
        this.vm = new FormGroup({
            name: new FormControl('', {
                validators: [
                    Validators.required,
                    DMValidators.asciiName,
                    DMValidators.rangeByteLength(this.nameMinLen, this.nameMaxLen),
                ],
            }),
            days: new FormControl(this.weekList.map((item) => item.value), {
                validators: [this.daysValidator],
            }),
            scheduleType: new FormControl(ScheduleTypeE.WEEKLY),
            durationType: new FormControl(DurationTypeE.CUSTOM),
            excludeFileType: new FormControl([], {
                validators: [
                    DMValidators.multiInputValidator(false, [
                        DMValidators.rangeByteLength(this.fileTypeMinLen, this.fileTypeMaxLen),
                        this.str.fileTypeValidator,
                    ]),
                    this.str.fileTypeNumValidator,
                    this.str.fileTypeLengthValidator,
                ],
            }),
            excludeFilePath: new FormControl([], {
                validators: [
                    DMValidators.multiInputValidator(false, [
                        DMValidators.rangeByteLength(1, this.str.filePathMaxLen),
                        this.str.filePathValidator,
                    ]),
                    this.str.filePathNumValidator,
                    this.str.filePathLengthValidator,
                ],
            }),
            restricted: new FormControl(false),
            restrictCapacity: new FormControl({ value: undefined, unit: Consts.MB }, {
                validators: [
                    DMValidators.capacityValidator(
                        true,
                        this.fileSizeMin,
                        this.fileSizeMax,
                    ),
                ],
            }),
            executePeriod: new FormControl(1),
            startTime: new FormControl(this.defaultStartTime),
            durationHour: new FormControl(this.str.defaultDurationHour),
            durationMinute: new FormControl(this.str.defaultDurationMinute),
        }, { updateOn: 'blur' });

        this.errorMessage = {
            name: {
                required: this.toolService.requiredErr(this.i18n.name),
                asciiName: this.toolService.asciiNameErr(this.i18n.name),
                rangeByteLength: this.toolService.rangeByteLengthErr(this.nameMinLen, this.nameMaxLen),
            },
            excludeFileType: {
                fileTypeName: this.i18n.fileTypeNameErr,
                rangeByteLength: this.toolService.rangeByteLengthErr(this.fileTypeMinLen, this.fileTypeMaxLen),
                fileTypeNum: this.i18n.fileTypeNumErr,
                fileTypeLength: this.i18n.fileTypeLengthErr,
            },
            excludeFilePath: {
                filePathName: this.i18n.filePathNameErr,
                rangeByteLength: this.toolService.rangeByteLengthErr(1, this.str.filePathMaxLen),
                filePathNum: this.i18n.filePathNumErr,
                filePathLength: this.i18n.filePathLengthErr,
            },
            restrictCapacity: {
                required: this.i18n.capacityRangeErr(this.fileSizeMin, this.fileSizeMax),
                min: this.i18n.capacityRangeErr(this.fileSizeMin, this.fileSizeMax),
                max: this.i18n.capacityRangeErr(this.fileSizeMin, this.fileSizeMax),
            },
            days: {
                required: this.i18n.specifyTimeErr,
            },
        };

        this.detectDurationChange('durationHour', 'durationMinute');
        this.detectTypeChange();
    }

    /**
     * 初始化修改页面参数
     */
    initModifyValue(): void {
        const { value, unit } = Number.isInteger(this.utilService.adaptUnit(this.policyData.maxFileSize).value) ?
            this.utilService.adaptUnit(this.policyData.maxFileSize) :
            this.utilService.adaptUnit(this.policyData.maxFileSize, undefined, CAPACITY_UNIT.MB);

        const vmValue: PolicyVm = {
            name: this.policyData.name,
            excludeFileType: this.policyData.excludeFileType === UNPLACEHOLDER ? [] : this.policyData.excludeFileType.split(','),
            excludeFilePath: this.policyData.excludeFilePath === UNPLACEHOLDER ?
                [] :
                this.str.getExcludeFilePathVmValue(this.policyData.excludeFilePath),
            restricted: value !== 0,
            restrictCapacity: value === 0 ? { value: undefined, unit: CAPACITY_UNIT.MB } : { value, unit },
            scheduleType: Number(this.policyData.scheduleType),
        };

        if (vmValue.scheduleType === ScheduleTypeE.WEEKLY) {
            const durationArr = this.policyData.duration.split(':').map((a) => Number(a));
            vmValue.days = this.policyData.dayOfWeek.split(',').map((a) => Number(a));
            vmValue.startTime = this.policyData.startTime;
            vmValue.durationHour = durationArr[0];
            vmValue.durationMinute = durationArr[1];
        } else if (vmValue.scheduleType === ScheduleTypeE.PERIOD) {
            vmValue.executePeriod = Number(this.policyData.period);
            vmValue.startTime = this.policyData.startTime;

            if (this.policyData.duration === DurationTypeE.UNTILFINISHED) {
                vmValue.durationType = DurationTypeE.UNTILFINISHED;
            } else {
                const durationArr = this.policyData.duration.split(':').map((a) => Number(a));
                vmValue.durationType = DurationTypeE.CUSTOM;
                vmValue.durationHour = durationArr[0];
                vmValue.durationMinute = durationArr[1];
            }
        }

        Object.keys(vmValue).forEach((item) => {
            this.vm.get(item).setValue(vmValue[item]);
        });

        this.vm.restore();
    }

    /**
     * vm灰化亮化
     */
    checkVmDisabled(): void {
        const vm = this.vm;
        const vmValue = vm.getRawValue();

        if (vmValue.restricted) {
            vm.get('restrictCapacity').enable();
        } else {
            vm.get('restrictCapacity').disable();
        }

        if (vmValue.scheduleType === ScheduleTypeE.WEEKLY) {
            vm.get('days').enable();
            vm.get('durationHour').enable();
            vm.get('durationMinute').enable();
        } else {
            vm.get('days').disable();
        }

        if (vmValue.scheduleType === ScheduleTypeE.PERIOD) {
            if (vmValue.durationType === DurationTypeE.CUSTOM) {
                vm.get('durationHour').enable();
                vm.get('durationMinute').enable();
            } else {
                vm.get('durationHour').disable();
                vm.get('durationMinute').disable();
            }

            vm.get('executePeriod').enable();
            vm.get('durationType').enable();
        } else {
            vm.get('executePeriod').disable();
            vm.get('durationType').disable();
        }

        if (vmValue.scheduleType === ScheduleTypeE.MANUAL) {
            vm.get('executePeriod').disable();
            vm.get('durationType').disable();
            vm.get('durationHour').disable();
            vm.get('durationMinute').disable();
            vm.get('days').disable();
        }
    }

    /**
     * 调度类型和持续时间类型变化
     */
    onStrategyChange(): void {
        this.checkVmDisabled();
    }

    /**
     * 持续时间变化，持续时间范围30分钟-24小时
     */
    onDurationTimeChange(hourCtrlName: string, minuteCtrlName: string): void {
        const minute = this.vm.get(minuteCtrlName).value;
        const hour = this.vm.get(hourCtrlName).value;

        if (minute < 30 && hour === 0) {
            this.vm.get(minuteCtrlName).setValue(30);
        }

        if (hour === 24) {
            this.vm.get(minuteCtrlName).setValue(0);
        }
    }

    /**
     * 检测文件类型变化
     */
    detectTypeChange(): void {
        merge(
            this.vm.get('excludeFileType').valueChanges.pipe(distinctUntilChanged()),
        ).subscribe(() => {
            const fileType = this.vm.get('excludeFileType').value;
            if (/\s+/.test(fileType)) {
                const newFileType = fileType.map((item: string) => item.replace(/\s+/g, ''))
                    .filter((str: string) => Boolean(str));
                this.vm.get('excludeFileType').setValue(Array.from(new Set(newFileType)));
            }
        });
    }

    /**
     * 检测持续时间变化
     */
    detectDurationChange(hourCtrlName: string, minuteCtrlName: string): void {
        merge(
            this.vm.get(hourCtrlName).valueChanges.pipe(distinctUntilChanged()),
            this.vm.get(minuteCtrlName).valueChanges.pipe(distinctUntilChanged()),
        ).subscribe(() => {
            this.onDurationTimeChange(hourCtrlName, minuteCtrlName);
        });
    }

    /**
     * 获取最大扫描大小限制下发参数，下发单位为MB
     */
    getMaxFileSizeParam(): number {
        // 0视为不限制
        if (this.vm.value.restricted) {
            return this.utilService.adaptUnit(
                this.vm.value.restrictCapacity.value,
                this.vm.value.restrictCapacity.unit,
                Consts.MB,
            ).value;
        } else {
            return 0;
        }
    }

    /**
     * 获取创建下发参数
     */
    buildCreateCmds(): Command[] {
        const cmds: Command[] = [];
        const values = this.vm.value;

        const params: OnDemandPolicy = {
            name: values.name,
            excludeFileType: values.excludeFileType.join(','),
            excludeFilePath: this.str.getExcludeFilePathParam(values.excludeFilePath),
            maxFileSize: this.getMaxFileSizeParam(),
            scheduleType: values.scheduleType,
            vstoreId: this.vstoreId,
        };

        if (values.scheduleType !== ScheduleTypeE.MANUAL) {
            params.startTime = values.startTime;
        }

        if (values.scheduleType === ScheduleTypeE.WEEKLY) {
            params.dayOfWeek = values.days.join(',');
            params.duration = this.str.getDuration(this.vm.value.durationHour, this.vm.value.durationMinute);
        } else if (values.scheduleType === ScheduleTypeE.PERIOD) {
            params.duration = values.durationType === DurationTypeE.CUSTOM ?
                this.str.getDuration(this.vm.value.durationHour, this.vm.value.durationMinute) :
                DurationTypeE.UNTILFINISHED;
            params.period = values.executePeriod;
        }

        cmds.push({
            name: this.i18n.createOnDemandPolicyAction(params.name),
            action: this.service.createOnDemandPolicy(params, { restNameParams: [params.name], doException: false })
                .pipe(
                    tap(({ data }) => this.closeArgs = { ...this.closeArgs, data }),
                ),
        });

        return cmds;
    }

    /**
     * 获取修改下发参数
     */
    buildModifyCmds(): Command[] {
        const cmds: Command[] = [];
        const changedValue = this.vm.changedValue;
        let params: OnDemandPolicy = {};

        if (changedValue) {
            params = { ...this.getModifyParams(changedValue) };
        }


        cmds.push({
            name: this.i18n.modifyOnDemandPolicyAction(this.policyData.name),
            action: this.service.modifyOnDemandPolicy(params),
        });

        return cmds;
    }

    /**
     * 获取修改时的下发参数
     */
    getModifyParams(changedValue): OnDemandPolicy {
        const params: OnDemandPolicy = {};
        params.ID = this.policyData.ID;
        params.period = changedValue.executePeriod;
        params.startTime = changedValue.startTime;

        if (Object.prototype.hasOwnProperty.call(changedValue, 'excludeFileType')) {
            if (changedValue.excludeFileType.length) {
                params.excludeFileType = changedValue.excludeFileType.join(',');
            } else {
                params.clearExcludeFileType = 'yes';
            }
        }

        if (Object.prototype.hasOwnProperty.call(changedValue, 'excludeFilePath')) {
            if (changedValue.excludeFilePath.length) {
                params.excludeFilePath = this.str.getExcludeFilePathParam(changedValue.excludeFilePath);
            } else {
                params.clearExcludeFilePath = 'yes';
            }
        }

        if (Object.prototype.hasOwnProperty.call(changedValue, 'restricted') || Object.prototype.hasOwnProperty.call(changedValue, 'restrictCapacity')) {
            params.maxFileSize = this.getMaxFileSizeParam();
        }

        if (Object.prototype.hasOwnProperty.call(changedValue, 'days')) {
            params.dayOfWeek = changedValue.days.join(',');
        }

        if (Object.prototype.hasOwnProperty.call(changedValue, 'durationHour') ||
            Object.prototype.hasOwnProperty.call(changedValue, 'durationMinute') ||
            Object.prototype.hasOwnProperty.call(changedValue, 'durationType')
        ) {
            params.duration = this.vm.value.durationType === DurationTypeE.CUSTOM || this.vm.value.scheduleType === ScheduleTypeE.WEEKLY ?
                this.str.getDuration(this.vm.value.durationHour, this.vm.value.durationMinute) :
                DurationTypeE.UNTILFINISHED;
        }

        if (Object.prototype.hasOwnProperty.call(changedValue, 'scheduleType')) {
            params.scheduleType = changedValue.scheduleType;

            if (changedValue.scheduleType !== ScheduleTypeE.MANUAL) {
                params.startTime = this.vm.value.startTime;
            }

            if (changedValue.scheduleType === ScheduleTypeE.WEEKLY) {
                params.dayOfWeek = this.vm.value.days.join(',');
                params.duration = this.str.getDuration(this.vm.value.durationHour, this.vm.value.durationMinute);
            } else if (changedValue.scheduleType === ScheduleTypeE.PERIOD) {
                params.duration = this.vm.value.durationType === DurationTypeE.CUSTOM ?
                    this.str.getDuration(this.vm.value.durationHour, this.vm.value.durationMinute) :
                    DurationTypeE.UNTILFINISHED;
                params.period = this.vm.value.executePeriod;
            }
        }

        return params;
    }

    buildCmds(): Command[] {
        let cmds: Command[] = [];

        if (this.policyData) {
            cmds = this.buildModifyCmds();
        } else {
            cmds = this.buildCreateCmds();
        }

        return cmds;
    }

    /**
     * 星期校验
     */
    daysValidator = (control: AbstractControl): ValidationErrors | null => {
        if (control.value.length === 0) {
            return { required: true };
        }

        return null;
    };
}
